import { Box, Stack, Typography } from "@mui/material";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { Dayjs } from "dayjs";
import { ReactNode } from "react";
import {
  DEFAULT_OVERBAR_HEIGHT,
  overbarOffset,
  VideoTimelineEvent,
} from "./VideoTimelineEvent";
import { VideoTimelineAreaTrack } from "./VideoTimelineAreaTrack";
import {
  ShortLabelDetail,
  VideoTimelineShortLabelTrack,
} from "./VideoTimelineShortLabelTrack";

interface VideoTimelineEventLaneProps {
  lane: string | undefined;
  isHighlighted: boolean;
  events: ScrapEyesEvent[];
  videoStart: Dayjs;
  duration: number;
  activeEventIds: ReadonlySet<string>;
  selectedEventId?: string | null;
  onEventClick?: (eventId: string) => void;
  colorEventsBy?: "type" | "material";
  materialNameSource?: "labelling" | "raw";
  showShortLabels?: boolean;
  shortLabelDetail?: ShortLabelDetail;
  modifiedEventIds?: Set<string>;
  adjacentPreviewById?: ReadonlyMap<string, ReactNode>;
  highlightColorsByEventId?: ReadonlyMap<string, string | null>;
}

export const VideoTimelineEventLane = ({
  lane,
  isHighlighted,
  events,
  videoStart,
  duration,
  activeEventIds,
  selectedEventId,
  onEventClick,
  colorEventsBy,
  materialNameSource,
  showShortLabels,
  shortLabelDetail,
  modifiedEventIds,
  adjacentPreviewById,
  highlightColorsByEventId,
}: VideoTimelineEventLaneProps) => (
  <Stack role={lane === undefined ? undefined : "group"} aria-label={lane}>
    {lane === undefined ? null : (
      <Typography
        variant="caption"
        color={isHighlighted ? "primary.main" : "text.secondary"}
        sx={{ fontWeight: isHighlighted ? "fontWeightBold" : undefined }}
      >
        {events[0]?.laneLabel ?? lane}
      </Typography>
    )}
    {showShortLabels ? null : (
      <VideoTimelineAreaTrack
        events={events}
        eventType="lift"
        videoStart={videoStart}
        duration={duration}
      />
    )}
    <Box
      sx={{
        position: "relative",
        height: 10,
        mt: `${overbarOffset(DEFAULT_OVERBAR_HEIGHT)}px`,
      }}
    >
      {events.map((event, index) => (
        <VideoTimelineEvent
          key={event.id + event.start.valueOf().toString()}
          event={event}
          index={index}
          selectedEventId={selectedEventId}
          onEventClick={onEventClick}
          allEvents={events}
          videoStart={videoStart}
          duration={duration}
          colorEventsBy={colorEventsBy}
          materialNameSource={materialNameSource}
          isActive={activeEventIds.has(event.id)}
          isModified={modifiedEventIds?.has(event.id)}
          adjacentPreview={adjacentPreviewById?.get(event.id)}
          highlightColor={highlightColorsByEventId?.get(event.id)}
        />
      ))}
    </Box>
    {showShortLabels ? (
      <VideoTimelineShortLabelTrack
        events={events}
        videoStart={videoStart}
        duration={duration}
        activeEventIds={activeEventIds}
        onEventClick={onEventClick}
        shortLabelDetail={shortLabelDetail}
      />
    ) : (
      <VideoTimelineAreaTrack
        events={events}
        eventType="drop"
        videoStart={videoStart}
        duration={duration}
      />
    )}
  </Stack>
);
